<template>
	<div class="partner-card-wrapper mt-2">
		<div class="partner-card">
			<div class="partner-card-wrapper__link text-decoration-none" @click="toggleToPartner">
				<div class="partner-card__logo">
					<Avatar
						:size="52"
						:avatar="partnerInfoSection.partner_logo"
						:borderRadius="'square'"
						:color="'#ffffff'"
					/>
				</div>
				<div class="partner-card__info">
					<div class="partner-card__title">
						{{ partnerInfoSection.partner_name }}
					</div>
					<div
						v-if="
							partnerInfoSection.partner_location ||
							partnerInfoSection.partner_location_title
						"
						class="partner-card__subtitle d-flex align-center mt-1"
					>
						<Icon type="pin" :size="16" color="var(--location-color)" />
						<span class="partner-card__location-value ml-1">{{
							partnerInfoSection.partner_location_title ||
							partnerInfoSection.partner_location
						}}</span>
					</div>
				</div>
			</div>
			<div @click="bookmarksToggle" class="partner-card__bookmark">
				<Icon
					:type="bookmarkStatus ? 'bookmark' : 'bookmark_empty'"
					:size="24"
					color="var(--button-icon-tint)"
					class="exhibitor-page__icon"
				/>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Dwh from '@/mixins/Dwh'
import Avatar from '@/components/Avatar'
import { mapActions, mapGetters } from 'vuex'

Vue.use(VueScrollTo)

export default {
	name: 'PartnerCard',
	mixins: [Dwh],
	components: {
		Avatar,
	},
	props: {
		partner: {
			type: Object,
			default: () => ({}),
		},
		isMyBookmark: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			bookmarkStatus: null,
		}
	},
	watch: {
		partner: {
			handler() {
				this.bookmarkStatus = this.partnerInfoSection.bookmark.is_bookmarked
			},
			immediate: true,
		},
	},
	computed: {
		...mapGetters('event', ['exhibitorFollow']),
		...mapGetters('auth', ['profile']),
		currentUser() {
			return this.profile
		},
		partnerInfoSection() {
			return this.getBlockData('partnerInfo', {})
		},
	},
	methods: {
		...mapActions('event', ['toggleBookmark']),
		getBlockData(name, fallback = undefined) {
			const block = (this.partner.partnersList || []).find((b) => b.block_name === name)
			return block ? block.block_data : fallback
		},
		handlerBookMark() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_exhibitor_bookmark_list',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
		handleRemoveBookmark() {
			this.sendTrigger({
				category: 'Exhibitors',
				action: 'click_exhibitor_remove_bookmark_list',
				module: 'Events',
				title: this.partnerInfoSection.partner_name,
				id1: this.partnerInfoSection.partner_id,
				id2: this.$route.query.event,
			})
		},
		bookmarksToggle() {
			const newStatus = this.bookmarkStatus ? 0 : 1
			if (newStatus === 1) {
				this.handlerBookMark()
			} else {
				this.handleRemoveBookmark()
			}
			this.bookmarkStatus = newStatus

			this.toggleBookmark({
				user_id: this.currentUser.user_id,
				bookmark_entity_type: this.partnerInfoSection.bookmark.bookmark_entity_type,
				bookmark_status: newStatus,
				bookmark_entity_id: this.partnerInfoSection.bookmark.bookmark_entity_id,
			}).catch((e) => {
				console.error(e)
				this.bookmarkStatus = newStatus === 1 ? 0 : 1
			})
		},
		toggleToPartner() {
			if (this.isMyBookmark) {
				this.$emit(
					'dwhClickBookmarkItem',
					this.partnerInfoSection.partner_name,
					this.partnerInfoSection.partner_id,
					this.$route.query.source
				)
			} else {
				this.sendTrigger({
					category: 'Exhibitors',
					action: 'click_exhibitor_profile',
					module: 'Events',
					title: this.partnerInfoSection.partner_name,
					id1: this.partnerInfoSection.partner_id,
					id2: this.$route.query.event,
				})
			}
			this.$router.push({
				name: 'exhibitor-by-id',
				params: { id: this.partnerInfoSection.partner_id },
				query: {
					group: this.$route.params.id,
					event: this.$route.query.event,
				},
			})
		},
	},
}
</script>

<style lang="scss">
.partner-card-wrapper {
	display: flex;
	align-items: center;
	background: #ffffff;
	border: 1px solid #e2e2e2;
	border-radius: 8px;
	padding: 12px;
	cursor: pointer;
	text-decoration: none;

	&__link {
		display: flex;
		width: 100%;
		text-decoration: none;
	}

	.partner-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		&__title {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
			margin-bottom: 4px;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			text-overflow: ellipsis;
			word-break: break-word;
		}

		&__location-value {
			font-family: Inter, sans-serif;
			font-weight: 500;
			font-size: 13px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--location-color) !important;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
			text-overflow: ellipsis;
			word-break: break-word;
		}

		&__logo {
			width: 64px;
			height: 64px;
			border-radius: 4px;
			overflow: hidden;
			margin-right: 16px;

			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}

		&__info {
			flex: 1;

			&__subtitle {
				display: flex;
				align-items: center;
				font-family: Inter, sans-serif;
				font-weight: 500;
				font-size: 13px;
				line-height: 100%;
				letter-spacing: 0;

				svg {
					margin-right: 8px;
				}
			}
		}

		&__bookmark {
			margin-left: 16px;
			cursor: pointer;
			&:hover svg {
				transform: scale(1.1);
			}
		}
	}
}
</style>
